/**
 * Client capability keys (client app plan v2.3, §B4).
 *
 * Every decision a client company user can make has one named key. The server answers "may this
 * person use this key at this company?" with `public.client_can` (RLS, RPCs and edge functions all
 * ask it); the website and the app show or hide buttons from the same keys, which
 * `sync_user_session` puts in the session's `permissions` for the client side (from
 * `public.effective_client_permissions`). Role defaults are rows in `role_permissions`, so changing
 * who holds a key is a data change, and later per-company or per-person switches reach the session
 * without code changes here.
 *
 * Never branch on a client role name in new code: ask for the key.
 *
 * The list must equal the `client:*` rows seeded by migrations 20261031100000 and 20261129600000
 * (client:contact_trade, client app part 5); a test checks it.
 */

export const CLIENT_CAPABILITIES = {
  /** Workflow gates, variation approve/reject, completion, payout releases, milestones, re-quotes. */
  approve: 'client:approve',
  /** Accept/reject quotes and counter-offers; create/withdraw a counter. */
  quotes: 'client:quotes',
  /** Post, edit, publish, cancel/withdraw jobs; QR jobs; rectification/warranty; signature requests. */
  jobsManage: 'client:jobs_manage',
  /** Request a change to a confirmed date (incl. late), answer a trade's request, force-confirm. */
  reschedule: 'client:reschedule',
  /** Raise a dispute; dispute a variation. */
  dispute: 'client:dispute',
  /** Payment methods, BECS, billing portal, job payments. */
  paymentMethods: 'client:payment_methods',
  /** Invoices, receipts, refunds, billing status, financial dashboards. */
  financialDocs: 'client:financial_docs',
  /** Company profile, workflow templates and assignments, lead times, inductions. */
  companyManage: 'client:company_manage',
  /** Edit or delete documents colleagues uploaded (own documents are always allowed). */
  documentsDeleteAny: 'client:documents_delete_any',
  /** Answer or propose dates before a date is confirmed. */
  scheduleRespond: 'client:schedule_respond',
  /** Job chat; nudging approvers. */
  chat: 'client:chat',
  /** Upload job documents. */
  documentsUpload: 'client:documents_upload',
  /** Review the trade. */
  review: 'client:review',
  /** Message or call the site contact through Trade Connector. */
  contactSite: 'client:contact_site',
  /** Call the trade through Trade Connector (client app part 5). */
  contactTrade: 'client:contact_trade',
} as const;

export type ClientCapability = (typeof CLIENT_CAPABILITIES)[keyof typeof CLIENT_CAPABILITIES];

export const CLIENT_CAPABILITY_KEYS: readonly ClientCapability[] = Object.values(CLIENT_CAPABILITIES);

/** Role defaults, as seeded in role_permissions (Owners and Admins hold every key). */
export const COORDINATOR_DEFAULT_CAPABILITIES: readonly ClientCapability[] = [
  CLIENT_CAPABILITIES.scheduleRespond,
  CLIENT_CAPABILITIES.chat,
  CLIENT_CAPABILITIES.documentsUpload,
  CLIENT_CAPABILITIES.review,
  CLIENT_CAPABILITIES.contactSite,
  CLIENT_CAPABILITIES.contactTrade,
];

/** Error code the edge functions return (403) when a client capability is missing. */
export const CLIENT_CAPABILITY_DENIED = 'CLIENT_CAPABILITY_DENIED';

export function isClientCapability(value: unknown): value is ClientCapability {
  return typeof value === 'string' && (CLIENT_CAPABILITY_KEYS as readonly string[]).includes(value);
}

interface SessionWithPermissions {
  permissions?: readonly string[] | null;
}

/** Whether the session holds a client capability key (the session carries the active side only). */
export function canClient(
  session: SessionWithPermissions | null | undefined,
  key: ClientCapability,
): boolean {
  return (session?.permissions ?? []).includes(key);
}

interface ClientSession extends SessionWithPermissions {
  customer_id?: string | null;
}

/**
 * Whether the session may use a client capability on a job of `jobCustomerId`: the user belongs to
 * that client company and holds the key. Platform admins keep their own explicit branch.
 */
export function canClientOnCompany(
  session: ClientSession | null | undefined,
  jobCustomerId: string | null | undefined,
  key: ClientCapability,
): boolean {
  if (!session?.customer_id || !jobCustomerId || session.customer_id !== jobCustomerId) {
    return false;
  }
  return canClient(session, key);
}
